import React, { useCallback, useEffect, useRef, useState } from "react"
import { H2, H3, H5 } from "../atoms/Headings"
import { Link } from "../atoms/Link"
import arrowLeft from "../../images/tall-arrow-left.svg"
import arrowRight from "../../images/tall-arrow-right.svg"
import arrowLeftGrey from "../../images/tall-arrow-left-grey.svg"
import arrowLeftHover from "../../images/tall-arrow-left-hover.svg"
import arrowRightGrey from "../../images/tall-arrow-right-grey.svg"
import arrowRightHover from "../../images/tall-arrow-right-hover.svg"
import karbonLogo from "../../images/karbon-logo.svg"
import { ArrowButton } from "../atoms/ArrowButton"
import theme from "../../settings/theme"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import useIsClient from "@karbonhq/marketing-shared-utils/hooks/useIsClient"
import { CAROUSEL_LG_BREAKPOINT } from "./Jumbotron"
import PropTypes from "prop-types"

const LinkResolver = require("../../utils/linkResolver")

const BannerEl = styled.div`
  color: black;
  border-radius: 6px;
  background-color: ${props =>
    props.bgColour ? `${props.bgColour}` : `${props.theme.colors.ash}`};
  background-image: ${props =>
    props.bg ? `url("${props.bg?.file.url}")` : `url("${karbonLogo}")`};
  background-repeat: no-repeat;
  width: 100%;
  align-items: center;
  height: 240px;
  background-position: calc(100% - 16px) calc(100% - 16px);
  background-size: auto 120px;
  padding: 0;
  display: block;

  ${breakpoint("md")(css`
    width: 100%;
    height: 96px;
    display: flex;
    background-size: auto 60px;
    background-position: 16px center;
    align-items: center;
    padding: 0 16px 0 86px;
  `)}
`

export const Banner = props => (
  <BannerEl bg={props.bg}>
    <div
      css={css`
        padding: 24px 20px;

        ${breakpoint("md")(
          css`
            display: none;
          `
        )}
      `}
    >
      <H5
        css={css`
          letter-spacing: 0;
          margin-top: 0;
          margin-bottom: 4px;
          color: black;
        `}
      >
        {props.template.supertitle}
      </H5>
      <H2 mt={0}>{props.template.item?.title || props.template.title}</H2>
    </div>

    <H3
      mb={0}
      mt={0}
      css={css`
        font-weight: 400;
        line-height: 24px;
        display: none;

        ${breakpoint("md")(
          css`
            display: block;
          `
        )}
      `}
    >
      {props.template.item?.title || props.template.title}
    </H3>
  </BannerEl>
)

export const BannerDisplay = ({ templates }) => {
  const divRef = useRef()
  const totalColumns = Math.ceil(templates.length / 3)
  const [numColumnsDisplayed, setNumColumnsDisplayed] = useState(3)
  // let divWidth = 296
  const [divWidth, setDivWidth] = useState(296)
  const [containerWidth, setContainerWidth] = useState(500)
  const [translate, setTranslate] = useState(0)
  const [finalCol, setFinalCol] = useState(2)
  const isClient = useIsClient()

  const calculateTranslate = useCallback(
    final => {
      if (numColumnsDisplayed < totalColumns) {
        return (final - numColumnsDisplayed) * (divWidth + 16)
      }
      return 0
    },
    [numColumnsDisplayed, totalColumns, divWidth]
  )

  const goForward = () => {
    if (finalCol < totalColumns) {
      const final = finalCol + 1
      setFinalCol(final)
      setTranslate(calculateTranslate(final))
    }
  }

  const goBackward = () => {
    if (translate > 0) {
      const final = finalCol - 1
      setFinalCol(final)
      setTranslate(calculateTranslate(final))
    }
  }

  useEffect(() => {
    let display

    const setDisplay = () => {
      if (
        window.matchMedia(`(max-width: ${CAROUSEL_LG_BREAKPOINT}px)`).matches
      ) {
        display = 2
      } else if (
        window.matchMedia(
          `(min-width: ${theme.breakpoints.xs}px) and (max-width: ${
            theme.breakpoints.md - 1
          }px)`
        ).matches
      ) {
        display = 1
      } else {
        display = 3
      }

      // if the carousel is at the beginning, make sure final banner updates when the screen size changes
      if (translate === 0) {
        setFinalCol(display)
      }

      setNumColumnsDisplayed(display)
    }

    const setWidths = () => {
      // this 200 is just to account for the edge case of when search is being resized
      //  the divRef gets lost as the sidebar disappears but the component is still re-rendering
      const width =
        Math.floor(divRef.current?.getBoundingClientRect().width) || 200

      // calculate the size that each div should appear on the page
      const div = Math.floor((width - 16 * (display - 1)) / display)

      setDivWidth(div)

      setContainerWidth(width)
    }

    const set = () => {
      setDisplay()
      setWidths()
    }

    set()

    window.addEventListener("resize", set)

    return () => {
      window.removeEventListener("resize", set)
    }
  }, [divRef, templates.length, finalCol, translate])

  useEffect(() => {
    setTranslate(calculateTranslate(finalCol))
  }, [calculateTranslate, divWidth, finalCol])

  return (
    <>
      <div
        css={css`
          width: 100%;
          height: 1px;
          overflow: hidden;
        `}
        ref={divRef}
      />
      <div
        css={css`
          width: ${containerWidth}px;
          position: absolute;
          visibility: ${isClient ? `visible` : `hidden`};
        `}
      >
        {totalColumns > numColumnsDisplayed && (
          <div
            css={css`
              display: none;

              ${breakpoint("md")(
                css`
                  display: flex;
                  justify-content: space-between;
                  position: relative;
                  margin-left: -30px;
                  margin-right: -30px;
                  top: 144px;
                `
              )}
            `}
          >
            <ArrowButton
              css={css`
                background-image: url(${translate > 0
                  ? arrowLeft
                  : arrowLeftGrey});

                ${translate > 0 &&
                css`
                  &:hover {
                    background-image: url(${arrowLeftHover});
                  }
                `}
              `}
              onClick={goBackward}
            />
            <ArrowButton
              css={css`
                background-image: url(${finalCol < totalColumns
                  ? arrowRight
                  : arrowRightGrey});

                ${finalCol < totalColumns &&
                css`
                  &:hover {
                    background-image: url(${arrowRightHover});
                  }
                `}
              `}
              onClick={goForward}
            />
          </div>
        )}
      </div>
      <div
        css={css`
          overflow: hidden;
          max-width: ${containerWidth}px;
          visibility: ${isClient ? `visible` : `hidden`};
        `}
      >
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(${templates.length}, 343px);
            grid-gap: 8px;
            overflow-x: auto;
            grid-auto-flow: column;

            ${breakpoint("md")(
              () => css`
                grid-gap: 16px;
                overflow-x: visible;
                grid-template-columns: repeat(${totalColumns}, ${divWidth}px);
                grid-template-rows: repeat(3, 1fr);
                transform: translateX(-${translate}px);
                transition: transform 0.2s ease;

                ${templates.length < 7 &&
                css`
                  grid-template-columns: repeat(${numColumnsDisplayed}, 1fr);
                  grid-template-rows: auto;
                  grid-auto-flow: row;
                `}
              `
            )}
          `}
        >
          {templates.map(template => (
            <Link
              to={LinkResolver(
                template.item?.internal.type || template.internal.type,
                template.item?.slug || template.slug
              )}
              key={template.item?.contentfulId || template.contentfulId}
            >
              <Banner template={template} bg={template.item?.headerImage} />
            </Link>
          ))}
        </div>
      </div>
    </>
  )
}

BannerDisplay.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
}
